<script lang="ts">
  import { router } from './lib/router.svelte';
  import { adminSession } from './lib/session.svelte';
  import PublicHeader from './components/PublicHeader.svelte';
  import PublicFooter from './components/PublicFooter.svelte';
  import MetaConsent from './components/MetaConsent.svelte';
  import InstallApp from './components/InstallApp.svelte';
  import PublicPromotionBadges from './components/PublicPromotionBadges.svelte';
  import AdminLayout from './pages/admin/AdminLayout.svelte';
  import { applyRouteSeo } from './lib/seo';

  const pageLoaders = {
    home: () => import('./pages/public/Home.svelte'),
    packages: () => import('./pages/public/Packages.svelte'),
    packageDetail: () => import('./pages/public/PackageDetail.svelte'),
    about: () => import('./pages/public/About.svelte'),
    reviews: () => import('./pages/public/Reviews.svelte'),
    booking: () => import('./pages/public/Booking.svelte'),
    bookingSuccess: () => import('./pages/public/BookingSuccess.svelte'),
    bookingStatus: () => import('./pages/public/BookingStatus.svelte'),
    notFound: () => import('./pages/public/NotFound.svelte'),
    contentPage: () => import('./pages/public/ContentPage.svelte'),
    adminLogin: () => import('./pages/admin/AdminLogin.svelte'),
    adminDashboard: () => import('./pages/admin/AdminDashboard.svelte'),
    adminBookings: () => import('./pages/admin/AdminBookings.svelte'),
    adminBookingDetail: () => import('./pages/admin/AdminBookingDetail.svelte'),
    adminPackages: () => import('./pages/admin/AdminPackages.svelte'),
    adminInventory: () => import('./pages/admin/AdminInventory.svelte'),
    adminContent: () => import('./pages/admin/AdminContent.svelte'),
    adminPromotions: () => import('./pages/admin/AdminPromotions.svelte'),
    adminAgreements: () => import('./pages/admin/AdminAgreements.svelte'),
    adminReviews: () => import('./pages/admin/AdminReviews.svelte'),
    adminIntegrations: () => import('./pages/admin/AdminIntegrations.svelte'),
    adminSettings: () => import('./pages/admin/AdminSettings.svelte'),
  };
  type PageKey = keyof typeof pageLoaders;

  // Route chunks load on first visit only. Keeping the settled promise makes the
  // {#await} expression stable, so a page that stays mounted is never re-created.
  const pageCache = new Map<PageKey, ReturnType<(typeof pageLoaders)[PageKey]>>();
  function loadPage(key: PageKey) {
    let pending = pageCache.get(key);
    if (!pending) {
      pending = pageLoaders[key]();
      pageCache.set(key, pending);
    }
    return pending;
  }

  const path = $derived(router.state.path);
  // Pages with their own data (packages, CMS pages) refine this after they load.
  $effect(() => { applyRouteSeo(path); });
  const packageSlug = $derived(path.startsWith('/packages/') ? decodeURIComponent(path.slice('/packages/'.length)) : '');
  const bookingId = $derived(path.startsWith('/admin/bookings/') ? decodeURIComponent(path.slice('/admin/bookings/'.length)) : '');
  const isAdmin = $derived(path === '/admin' || path.startsWith('/admin/'));

  $effect(() => {
    if (isAdmin) void adminSession.ensureLoaded();
    const status = adminSession.status;
    if (status === 'unknown') return;
    if (isAdmin && path !== '/admin/login' && status !== 'authed') router.navigate('/admin/login', true);
    if (path === '/admin/login' && status === 'authed') router.navigate('/admin', true);
  });
</script>

{#if isAdmin}
  {#if path === '/admin/login'}
    {#await loadPage('adminLogin') then { default: Page }}<Page />{/await}
  {:else if adminSession.status === 'unknown'}
    <div class="d-flex justify-content-center align-items-center vh-100 text-secondary" role="status">Checking your session…</div>
  {:else if adminSession.authenticated}
    <AdminLayout>
      {#if path === '/admin'}{#await loadPage('adminDashboard') then { default: Page }}<Page />{/await}
      {:else if path === '/admin/bookings'}{#await loadPage('adminBookings') then { default: Page }}<Page />{/await}
      {:else if bookingId}{#await loadPage('adminBookingDetail') then { default: Page }}<Page id={bookingId} />{/await}
      {:else if path === '/admin/packages'}{#await loadPage('adminPackages') then { default: Page }}<Page />{/await}
      {:else if path === '/admin/inventory'}{#await loadPage('adminInventory') then { default: Page }}<Page />{/await}
      {:else if path === '/admin/content'}{#await loadPage('adminContent') then { default: Page }}<Page />{/await}
      {:else if path === '/admin/promotions'}{#await loadPage('adminPromotions') then { default: Page }}<Page />{/await}
      {:else if path === '/admin/agreements'}{#await loadPage('adminAgreements') then { default: Page }}<Page />{/await}
      {:else if path === '/admin/reviews'}{#await loadPage('adminReviews') then { default: Page }}<Page />{/await}
      {:else if path === '/admin/integrations'}{#await loadPage('adminIntegrations') then { default: Page }}<Page />{/await}
      {:else if path === '/admin/settings'}{#await loadPage('adminSettings') then { default: Page }}<Page />{/await}
      {:else}{#await loadPage('adminDashboard') then { default: Page }}<Page />{/await}{/if}
    </AdminLayout>
  {/if}
{:else}
  <PublicHeader />
  <PublicPromotionBadges />
  <a class="skip-link" href="#main-content">Skip to main content</a>
  <main id="main-content" tabindex="-1">
    {#if path === '/'}{#await loadPage('home') then { default: Page }}<Page />{/await}
    {:else if path === '/packages'}{#await loadPage('packages') then { default: Page }}<Page />{/await}
    {:else if packageSlug}{#await loadPage('packageDetail') then { default: Page }}<Page slug={packageSlug} />{/await}
    {:else if path === '/about'}{#await loadPage('about') then { default: Page }}<Page />{/await}
    {:else if path === '/reviews'}{#await loadPage('reviews') then { default: Page }}<Page />{/await}
    {:else if path === '/book'}{#await loadPage('booking') then { default: Page }}<Page />{/await}
    {:else if path === '/booking/success'}{#await loadPage('bookingSuccess') then { default: Page }}<Page />{/await}
    {:else if path === '/booking/status'}{#await loadPage('bookingStatus') then { default: Page }}<Page />{/await}
    {:else if path === '/404'}{#await loadPage('notFound') then { default: Page }}<Page />{/await}
    {:else}{#await loadPage('contentPage') then { default: Page }}<Page slug={path.replace(/^\/+|\/+$/g, '')} />{/await}{/if}
  </main>
  <PublicFooter />
  <MetaConsent />
  <InstallApp />
{/if}
